import { useState } from "react";
import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getMyAdminStatus } from "@/lib/billing.functions";
import {
  addSuppressedContactAdmin,
  listSuppressedContactsAdmin,
  type SuppressedContactRow,
} from "@/lib/suppression.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/_authed/admin/suppression")({
  head: () => ({
    meta: [
      { title: "Admin: Contact suppression: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: async () => {
    if (typeof window === "undefined") return;
    const status = await getMyAdminStatus();
    if (!status.isAdmin) throw redirect({ to: "/dashboard" });
  },
  component: AdminSuppressionPage,
});

function AdminSuppressionPage() {
  const qc = useQueryClient();
  const fetchList = useServerFn(listSuppressedContactsAdmin);
  const addFn = useServerFn(addSuppressedContactAdmin);

  const list = useQuery({
    queryKey: ["admin-suppressed-contacts"],
    queryFn: () => fetchList(),
  });

  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [firmName, setFirmName] = useState("");
  const [notes, setNotes] = useState("");
  const [feedback, setFeedback] = useState<
    { kind: "ok" | "err"; text: string } | null
  >(null);

  const addMut = useMutation({
    mutationFn: () =>
      addFn({
        data: {
          email: email.trim(),
          fullName: fullName.trim() || undefined,
          firmName: firmName.trim() || undefined,
          notes: notes.trim() || undefined,
        },
      }),
    onSuccess: () => {
      setFeedback({ kind: "ok", text: `Added ${email.trim()} to suppression list.` });
      setEmail("");
      setFullName("");
      setFirmName("");
      setNotes("");
      qc.invalidateQueries({ queryKey: ["admin-suppressed-contacts"] });
    },
    onError: (e) =>
      setFeedback({
        kind: "err",
        text: e instanceof Error ? e.message : "Failed to add",
      }),
  });

  const rows: SuppressedContactRow[] = list.data?.rows ?? [];

  return (
    <main className="mx-auto max-w-[840px] px-4 pb-16 pt-6">
      <header className="mb-6">
        <Link
          to="/admin"
          className="text-[12.5px] text-brand-muted no-underline hover:text-brand-ink"
        >
          ← Back to admin
        </Link>
        <h1 className="mt-2 font-serif text-[32px] font-normal leading-tight tracking-tight text-brand-ink">
          Contact suppression.
        </h1>
        <p className="mt-1 text-[14px] text-brand-muted">
          GDPR/CCPA removal requests. Suppressed contacts are filtered from
          every path that serves contact records. Source rows stay in place so
          refreshes and reimports cannot resurrect a suppression.
        </p>
      </header>

      <section className="mb-8 rounded-2xl border border-brand-line bg-brand-paper p-5">
        <h2 className="mb-3 font-serif text-[20px] text-brand-ink">
          Add a suppression
        </h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setFeedback(null);
            addMut.mutate();
          }}
          className="space-y-3"
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="sup-email">Email</Label>
              <Input
                id="sup-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="person@example.com"
                autoComplete="off"
                required
              />
            </div>
            <div>
              <Label htmlFor="sup-name">Full name</Label>
              <Input
                id="sup-name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Optional"
                autoComplete="off"
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="sup-firm">Firm</Label>
              <Input
                id="sup-firm"
                value={firmName}
                onChange={(e) => setFirmName(e.target.value)}
                placeholder="Optional"
                autoComplete="off"
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="sup-notes">Notes</Label>
              <Textarea
                id="sup-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Ticket link, source of request, or other context"
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={addMut.isPending || !email.trim()}
          >
            {addMut.isPending ? "Adding…" : "Add suppression"}
          </Button>
          {feedback && (
            <p
              className={
                feedback.kind === "ok"
                  ? "text-[12.5px] text-brand-muted"
                  : "text-[12.5px] text-destructive"
              }
            >
              {feedback.text}
            </p>
          )}
        </form>
      </section>

      <section className="rounded-2xl border border-brand-line bg-brand-paper p-5">
        <h2 className="mb-1 font-serif text-[20px] text-brand-ink">
          Current suppressions
        </h2>
        <p className="mb-3 text-[12.5px] text-brand-muted">
          {rows.length} contact{rows.length === 1 ? "" : "s"} suppressed.
        </p>
        {list.isLoading ? (
          <p className="text-[14px] text-brand-muted">Loading…</p>
        ) : list.error ? (
          <p className="text-[12.5px] text-destructive">
            {list.error instanceof Error
              ? list.error.message
              : "Failed to load"}
          </p>
        ) : rows.length === 0 ? (
          <p className="text-[14px] text-brand-muted">
            No suppressions on file.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-brand-line">
            <table className="w-full text-[13px]">
              <thead className="bg-brand-paper-2 text-left text-[11.5px] uppercase tracking-wider text-brand-muted">
                <tr>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Firm</th>
                  <th className="px-3 py-2">Reason</th>
                  <th className="px-3 py-2">Requested</th>
                  <th className="px-3 py-2">Notes</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-brand-line">
                    <td className="px-3 py-2 font-mono">{r.email}</td>
                    <td className="px-3 py-2">{r.full_name ?? ""}</td>
                    <td className="px-3 py-2">{r.firm_name ?? ""}</td>
                    <td className="px-3 py-2">{r.reason ?? ""}</td>
                    <td className="px-3 py-2 text-brand-muted">
                      {new Date(r.requested_at).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </td>
                    <td className="px-3 py-2 text-brand-muted">
                      {r.notes ?? ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
}